import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change_Current_Location, changeHeaderTitle } from '../store';
import axios from 'axios';
import { Banknote, ArrowRightLeft, ArrowRight, RefreshCw, AlertCircle, CheckCircle, Info } from 'lucide-react';

function MasterAccount() {
  const dispatch = useDispatch();
  const MyData = useSelector((state) => state.MyData);

  const [loading, setLoading] = useState(false);
  const [wizzBalance, setWizzBalance] = useState(null);       // 위즈페이 모계좌 잔액
  const [dozenBalance, setDozenBalance] = useState(null);     // 더즌 모계좌 잔액
  const [transferAmount, setTransferAmount] = useState('');
  const [rvAccountMemo, setRvAccountMemo] = useState('');
  const [transferHistory, setTransferHistory] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    dispatch(change_Current_Location("위즈페이 계좌"));
    dispatch(changeHeaderTitle({ mainTitle: "위즈페이 계좌", subTitle: "모계좌 잔액 조회 및 이체" }));

    loadWizzBalance();
    loadDozenBalance();
    loadTransferHistory();
  }, []);

  // 토스트 메시지 자동 사라짐
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  // 위즈페이 예치금 잔액 조회
  const loadWizzBalance = async () => {
    try {
      const response = await axios.post(process.env.REACT_APP_API + "/api/wizzpay-transfer/balance", {}, {
        withCredentials: true,
      });

      if (response.data.code === 1000) {
        setWizzBalance(Number(response.data.data.amount));
      } else {
        console.error('위즈페이 잔액 조회 실패:', response.data.msg);
      }
    } catch (error) {
      console.error('위즈페이 잔액 조회 오류:', error);
    }
  };

  // 더즌 모계좌 잔액 조회
  const loadDozenBalance = async () => {
    try {
      const response = await axios.post(process.env.REACT_APP_API + "/api/transfer/balance", {}, {
        withCredentials: true,
      });

      if (response.data.code === 1000) {
        const data = response.data.data;
        setDozenBalance({
          accountNumber: data.account || data.tmnId || '',
          bankCode: data.bank_code || '',
          availableBalance: data.result
            ? data.result.balance_amount
            : Number(data.available_balance) || 0
        });
      }
    } catch (error) {
      console.error('더즌 잔액 조회 오류:', error);
    }
  };

  // 잔액 새로고침
  const refreshBalances = async () => {
    setLoading(true);
    await Promise.all([loadWizzBalance(), loadDozenBalance()]);
    setLoading(false);
  };

  // UUID 생성
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // 이체 실행 버튼 클릭 (확인 모달 표시)
  const handleTransferClick = () => {
    if (!transferAmount) {
      setMessage({ type: 'error', text: '이체 금액을 입력해주세요.' });
      return;
    }

    const amount = Number(transferAmount);

    if (amount <= 0) {
      setMessage({ type: 'error', text: '이체 금액은 0원보다 커야 합니다.' });
      return;
    }

    if (wizzBalance !== null && amount > wizzBalance) {
      setMessage({ type: 'error', text: '이체 금액이 위즈페이 예치금 잔액을 초과합니다.' });
      return;
    }

    setShowConfirmModal(true);
  };

  // 실제 이체 실행
  const handleExecuteTransfer = async () => {
    if (!dozenBalance) {
      setMessage({ type: 'error', text: '더즌 모계좌 정보를 불러오지 못했습니다.' });
      return;
    }

    try {
      setLoading(true);
      setShowConfirmModal(false);

      const response = await axios.post(process.env.REACT_APP_API + "/api/wizzpay-transfer/send", {
        rvBankCode: dozenBalance.bankCode,
        rvAccountNo: dozenBalance.accountNumber,
        rvAccountMemo: rvAccountMemo || "자금통합",
        amount: String(transferAmount),
        transferReason: "위즈페이→더즌 자금통합"
      }, {
        withCredentials: true,
        headers: {
          'Idempotency-Key': generateUUID()
        }
      });

      const { code, msg } = response.data;

      if (code === 1000) {
        setMessage({ type: 'success', text: msg || '송금이 완료되었습니다.' });
        setTransferAmount('');
        setRvAccountMemo('');
        refreshBalances();
        loadTransferHistory();
      } else if (code === 2000) {
        setMessage({ type: 'warning', text: msg || '송금 처리 중입니다. 이체 내역에서 결과를 확인해주세요.' });
        setTransferAmount('');
        setRvAccountMemo('');
        loadTransferHistory();
      } else {
        setMessage({ type: 'error', text: msg || '송금에 실패했습니다.' });
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setMessage({ type: 'error', text: '처리 중입니다. 잠시 후 다시 확인해주세요.' });
      } else {
        console.error('송금 오류:', error);
        setMessage({ type: 'error', text: '송금 중 오류가 발생했습니다.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // 이체 결과 조회
  const handleCheckTransfer = async (item) => {
    try {
      setLoading(true);
      const response = await axios.post(process.env.REACT_APP_API + "/api/wizzpay-transfer/check", {
        orgTelNo: item.telegram_no,
        trDt: item.tr_dt,
        logId: item.id
      }, {
        withCredentials: true,
      });

      const { code, msg } = response.data;

      if (code === 1000) {
        setMessage({ type: 'success', text: msg || '이체 성공 확인' });
        loadTransferHistory();
        refreshBalances();
      } else if (code === 2000) {
        setMessage({ type: 'warning', text: msg || '아직 처리 중입니다.' });
      } else {
        setMessage({ type: 'error', text: msg || '결과 조회에 실패했습니다.' });
      }
    } catch (error) {
      console.error('결과 조회 오류:', error);
      setMessage({ type: 'error', text: '결과 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  // 이체 내역 조회
  const loadTransferHistory = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API + "/api/wizzpay-transfer/logs", {
        withCredentials: true,
      });

      if (response.data.code < 2000) {
        setTransferHistory(response.data.data?.logs || []);
      }
    } catch (error) {
      console.error('이체 내역 조회 오류:', error);
    }
  };

  // 금액 포맷팅
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  // 날짜 포맷팅 (한국 시간으로 변환)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const utcTime = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcTime).toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // 적요 입력 핸들러 (7글자 제한)
  const handleRvAccountMemoChange = (e) => {
    const value = e.target.value;
    if (value.length <= 7) {
      setRvAccountMemo(value);
    } else {
      setRvAccountMemo(value.substring(0, 7));
    }
  };

  return (
    <div className="master-account-container">
      <div className="page-header">
        <h1>위즈페이 계좌</h1>
        <p>모계좌 잔액 조회 및 이체</p>
      </div>

      {/* 이체 흐름 + 잔액 */}
      <div className="transfer-flow-box">
        <div className="transfer-flow">
          <div className="flow-account">
            <span className="flow-label">출금</span>
            <span className="flow-name">위즈페이 모계좌</span>
            {wizzBalance !== null ? (
              <span className="flow-balance">{formatAmount(wizzBalance)}원</span>
            ) : (
              <span className="flow-balance loading">조회 중...</span>
            )}
          </div>
          <div className="flow-arrow">
            <ArrowRight size={22} />
          </div>
          <div className="flow-account">
            <span className="flow-label">입금</span>
            <span className="flow-name">더즌 모계좌</span>
            {dozenBalance ? (
              <span className="flow-balance">{formatAmount(dozenBalance.availableBalance)}원</span>
            ) : (
              <span className="flow-balance loading">조회 중...</span>
            )}
          </div>
        </div>
        <button
          className="btn-refresh-small flow-refresh"
          onClick={refreshBalances}
          disabled={loading}
          title="잔액 새로고침"
        >
          <RefreshCw size={14} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {/* 안내 문구 */}
      <div className="notice-box">
        <div className="notice-icon">
          <Info size={18} />
        </div>
        <ul className="notice-list">
          <li>위즈페이 정산 완료 확인 후 이체를 진행해주세요.</li>
        </ul>
      </div>

      {/* 토스트 메시지 */}
      {message.text && (
        <div className={`toast ${message.type === 'success' ? 'toast-success' : message.type === 'warning' ? 'toast-warning' : 'toast-error'}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {message.type === 'success' ? <CheckCircle size={20} /> : message.type === 'warning' ? <Info size={20} /> : <AlertCircle size={20} />}
            </div>
            <div className="toast-message">
              <div className="toast-title">
                {message.type === 'success' ? '성공' : message.type === 'warning' ? '처리중' : '오류'}
              </div>
              <div className="toast-text">{message.text}</div>
            </div>
            <button
              className="toast-close"
              onClick={() => setMessage({ type: '', text: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="settlement-grid">

        {/* 이체 폼 */}
        <div className="card">
          <div className="card-header">
            <ArrowRightLeft size={24} />
            <h3>이체</h3>
          </div>
          <div className="card-content">
            <div className="transfer-form">
              <div className="form-group">
                <label>이체 금액 (원)</label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="이체할 금액을 입력하세요"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  적요
                  <span className="tooltip-container">
                    <Info size={16} className="tooltip-icon" />
                    <span className="tooltip-text">해당 계좌에 찍히는 문구</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={rvAccountMemo}
                  onChange={handleRvAccountMemoChange}
                  placeholder="적요를 입력하세요 (7글자 이내)"
                  disabled={loading}
                />
              </div>

              <button
                className="btn-transfer"
                onClick={handleTransferClick}
                disabled={loading || !transferAmount}
              >
                {loading ? '처리 중...' : '이체 실행'}
              </button>
            </div>
          </div>
        </div>

        {/* 이체 내역 */}
        <div className="card">
          <div className="card-header">
            <h3>이체 내역</h3>
            <button
              className="btn-refresh"
              onClick={loadTransferHistory}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            </button>
          </div>
          <div className="card-content">
            {transferHistory.length > 0 ? (
              <>
                {/* 데스크톱용 테이블 */}
                <div className="transfer-history desktop-only">
                  <table>
                    <thead>
                      <tr>
                        <th>이체일시</th>
                        <th>이체금액</th>
                        <th>적요</th>
                        <th>상태</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferHistory.map((item, index) => (
                        <tr key={item.id || index}>
                          <td>{formatDate(item.created_at)}</td>
                          <td className="amount">{formatAmount(item.amount)}원</td>
                          <td>{item.rv_account_cntn || '-'}</td>
                          <td>
                            <span className={`status ${
                              item.transfer_status === 'SUCCESS' ? 'status-success' :
                              item.transfer_status === 'PROCESSING' ? 'status-pending' :
                              item.transfer_status === 'FAILED' ? 'status-failed' : 'status-unknown'
                            }`}>
                              {item.transfer_status === 'SUCCESS' ? '성공' :
                               item.transfer_status === 'PROCESSING' ? '처리중' :
                               item.transfer_status === 'FAILED' ? '실패' : '알 수 없음'}
                            </span>
                          </td>
                          <td>
                            {item.transfer_status === 'PROCESSING' && (
                              <button
                                className="btn-check-result"
                                onClick={() => handleCheckTransfer(item)}
                                disabled={loading}
                              >
                                결과조회
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 모바일용 카드 리스트 */}
                <div className="transfer-cards mobile-only">
                  {transferHistory.map((item, index) => (
                    <div className="transfer-card" key={item.id || index}>
                      <div className="transfer-card-header">
                        <span className="transfer-card-date">{formatDate(item.created_at)}</span>
                        <span className={`status ${
                          item.transfer_status === 'SUCCESS' ? 'status-success' :
                          item.transfer_status === 'PROCESSING' ? 'status-pending' :
                          item.transfer_status === 'FAILED' ? 'status-failed' : 'status-unknown'
                        }`}>
                          {item.transfer_status === 'SUCCESS' ? '성공' :
                           item.transfer_status === 'PROCESSING' ? '처리중' :
                           item.transfer_status === 'FAILED' ? '실패' : '알 수 없음'}
                        </span>
                      </div>
                      <div className="transfer-card-amount">
                        {formatAmount(item.amount)}원
                      </div>
                      <div className="transfer-card-details">
                        <div className="transfer-card-row">
                          <span className="label">적요</span>
                          <span className="value">{item.rv_account_cntn || '-'}</span>
                        </div>
                        {item.transfer_status === 'PROCESSING' && (
                          <button
                            className="btn-check-result"
                            onClick={() => handleCheckTransfer(item)}
                            disabled={loading}
                            style={{ marginTop: '8px' }}
                          >
                            결과조회
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-state">이체 내역이 없습니다.</div>
            )}
          </div>
        </div>
      </div>

      {/* 이체 확인 모달 */}
      {showConfirmModal && (
        <div className="otp-modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="otp-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="otp-modal-header">
              <h3>이체 확인</h3>
              <button
                className="otp-modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                ×
              </button>
            </div>
            <div className="otp-modal-body">
              <div className="confirm-details">
                <div className="confirm-row">
                  <span className="confirm-label">이체 금액</span>
                  <span className="confirm-value confirm-amount">{formatAmount(transferAmount)}원</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">적요</span>
                  <span className="confirm-value">{rvAccountMemo || '자금통합'}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">경로</span>
                  <span className="confirm-value">위즈페이 → 더즌 모계좌</span>
                </div>
              </div>
              <p style={{ marginTop: '16px', color: '#ef4444', fontSize: '14px', fontWeight: '500' }}>
                위 내용으로 이체를 진행하시겠습니까?
              </p>
            </div>
            <div className="otp-modal-footer">
              <button
                className="btn-transfer"
                onClick={handleExecuteTransfer}
                disabled={loading}
              >
                {loading ? '처리 중...' : '이체 실행'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowConfirmModal(false)}
                disabled={loading}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .master-account-container {
          padding: 20px;
          margin: 0 auto;
          margin-top: 80px;
        }

        .page-header {
          margin-bottom: 30px;
        }

        .page-header h1 {
          font-size: 28px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
          text-align: left;
        }

        .page-header p {
          color: #6b7280;
          font-size: 16px;
          text-align: left;
        }

        .toast {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1000;
          max-width: 400px;
          min-width: 300px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-content {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border-radius: 8px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          backdrop-filter: blur(10px);
        }

        .toast-success .toast-content {
          background: rgba(16, 185, 129, 0.95);
          color: white;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }

        .toast-warning .toast-content {
          background: rgba(245, 158, 11, 0.95);
          color: white;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .toast-error .toast-content {
          background: rgba(239, 68, 68, 0.95);
          color: white;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .toast-icon {
          flex-shrink: 0;
          margin-top: 2px;
        }

        .toast-message {
          flex: 1;
        }

        .toast-title {
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 4px;
        }

        .toast-text {
          font-size: 13px;
          opacity: 0.9;
          line-height: 1.4;
        }

        .toast-close {
          background: none;
          border: none;
          color: white;
          font-size: 18px;
          font-weight: bold;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .toast-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .transfer-flow-box {
          position: relative;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .transfer-flow {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
        }

        .flow-account {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 16px 28px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          min-width: 160px;
        }

        .flow-label {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .flow-name {
          font-size: 15px;
          color: #1f2937;
          font-weight: 600;
        }

        .flow-balance {
          font-size: 18px;
          color: #059669;
          font-weight: 700;
          margin-top: 4px;
        }

        .flow-balance.loading {
          font-size: 13px;
          color: #9ca3af;
          font-weight: 400;
        }

        .flow-balance.pending {
          font-size: 13px;
          color: #9ca3af;
          font-weight: 400;
        }

        .flow-arrow {
          color: #3b82f6;
          display: flex;
          align-items: center;
        }

        .flow-refresh {
          position: absolute;
          top: 12px;
          right: 12px;
        }

        .notice-box {
          display: flex;
          gap: 10px;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          padding: 14px 18px;
          margin-bottom: 24px;
        }

        .notice-icon {
          flex-shrink: 0;
          color: #3b82f6;
          margin-top: 2px;
        }

        .notice-list {
          margin: 0;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .notice-list li {
          font-size: 14px;
          color: #1e40af;
          line-height: 1.5;
        }

        .notice-list li::before {
          content: "•";
          margin-right: 6px;
          color: #3b82f6;
        }

        .btn-refresh-small {
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          padding: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .btn-refresh-small:hover {
          background: #e5e7eb;
        }

        .btn-refresh-small:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .settlement-grid {
          display: grid;
          grid-template-columns: 35% 65%;
          gap: 20px;
          margin-bottom: 30px;
        }

        .card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .card-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .btn-refresh {
          margin-left: auto;
          background: none;
          border: none;
          padding: 8px;
          border-radius: 6px;
          cursor: pointer;
          color: #6b7280;
          transition: all 0.2s;
        }

        .btn-refresh:hover {
          background: #e2e8f0;
          color: #374151;
        }

        .btn-refresh:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .card-content {
          padding: 20px;
        }

        .transfer-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-weight: 500;
          color: #374151;
        }

        .tooltip-container {
          position: relative;
          display: inline-flex;
          align-items: center;
        }

        .tooltip-icon {
          color: #6b7280;
          cursor: help;
          transition: color 0.2s;
        }

        .tooltip-icon:hover {
          color: #3b82f6;
        }

        .tooltip-text {
          visibility: hidden;
          opacity: 0;
          position: absolute;
          bottom: 125%;
          left: 0;
          transform: translateX(0);
          margin-left: 4px;
          background-color: #1f2937;
          color: white;
          text-align: center;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 10000;
          transition: opacity 0.2s, visibility 0.2s;
          pointer-events: none;
        }

        .tooltip-text::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 8px;
          transform: translateX(0);
          border: 5px solid transparent;
          border-top-color: #1f2937;
        }

        .tooltip-container:hover .tooltip-text {
          visibility: visible;
          opacity: 1;
        }

        .form-group input {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-group input:disabled {
          background-color: #f9fafb;
          color: #6b7280;
        }

        .btn-transfer {
          padding: 16px;
          background: #059669;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-transfer:hover:not(:disabled) {
          background: #047857;
        }

        .btn-transfer:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .desktop-only {
          display: block !important;
        }

        .mobile-only {
          display: none !important;
        }

        .transfer-history table {
          width: 100%;
          border-collapse: collapse;
        }

        .transfer-history th,
        .transfer-history td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }

        .transfer-history th {
          background: #f9fafb;
          font-weight: 600;
          color: #374151;
        }

        .transfer-history .amount {
          font-weight: 600;
          color: #059669;
        }

        .transfer-cards {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .transfer-card {
          background: #f9fafb;
          border-radius: 12px;
          padding: 16px;
          border: 1px solid #e5e7eb;
        }

        .transfer-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .transfer-card-date {
          font-size: 13px;
          color: #6b7280;
        }

        .transfer-card-amount {
          font-size: 20px;
          font-weight: 700;
          color: #059669;
          margin-bottom: 12px;
        }

        .transfer-card-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .transfer-card-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
        }

        .transfer-card-row .label {
          color: #6b7280;
        }

        .transfer-card-row .value {
          color: #1f2937;
          font-weight: 500;
        }

        .status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-success {
          background: #d1fae5;
          color: #065f46;
        }

        .status-pending {
          background: #fef3c7;
          color: #92400e;
        }

        .status-failed {
          background: #fee2e2;
          color: #991b1b;
        }

        .status-unknown {
          background: #f3f4f6;
          color: #6b7280;
        }

        .btn-check-result {
          padding: 4px 10px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-check-result:hover:not(:disabled) {
          background: #2563eb;
        }

        .btn-check-result:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .confirm-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .confirm-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .confirm-label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }

        .confirm-value {
          font-size: 14px;
          color: #1f2937;
          font-weight: 600;
        }

        .confirm-amount {
          font-size: 18px;
          color: #059669;
          font-weight: 700;
        }

        .empty-state {
          text-align: left;
          color: #6b7280;
          padding: 40px;
        }

        .otp-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .otp-modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 400px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .otp-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .otp-modal-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
        }

        .otp-modal-close {
          background: none;
          border: none;
          font-size: 28px;
          color: #6b7280;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .otp-modal-close:hover {
          background: #f3f4f6;
        }

        .otp-modal-body {
          padding: 20px;
        }

        .otp-modal-footer {
          display: flex;
          gap: 10px;
          padding: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .otp-modal-footer .btn-transfer {
          flex: 1;
          margin: 0;
        }

        .btn-cancel {
          padding: 16px 24px;
          background: #f3f4f6;
          color: #374151;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .btn-cancel:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .account-details {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .balance-summary {
            flex-direction: column;
            gap: 8px;
            align-items: flex-start;
          }

          .settlement-grid {
            grid-template-columns: 1fr;
          }

          .master-account-container {
            padding: 16px;
          }

          .desktop-only {
            display: none !important;
          }

          .mobile-only {
            display: flex !important;
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
}

export default MasterAccount;
